import { type Editor, EditorContent, useEditorState } from "@tiptap/react";
import clsx from "clsx";
import React from "react";
import Button from "../../common/Button/Button";
import Icon, { type IconName } from "../../common/Icon/Icon";
import Toolbar from "../../common/Toolbar/Toolbar";
import styles from "./RichTextEditor.module.scss";
import { RichTextEditorColor } from "./RichTextEditorColor";
import { RichTextEditorLink, useLinkHoverCard } from "./RichTextEditorLink";
import {
  RichTextEditorAlign,
  RichTextEditorLists,
  RichTextEditorMenu,
  RichTextEditorTurnInto,
} from "./RichTextEditorMenu";
import { RichTextEditorContext, preserveSelection, useRichTextEditor } from "./context";
import "../../../styles/focus-ring.scss";

export { preserveSelection, useRichTextEditor } from "./context";
export type { RichTextEditorContextValue } from "./context";
export { ALIGN_OPTIONS, LIST_OPTIONS, TURN_INTO_OPTIONS } from "./RichTextEditorMenu";
export type { RichTextEditorMenuOption, RichTextEditorMenuProps } from "./RichTextEditorMenu";
export { normalizeHref, saveLink } from "./RichTextEditorLink";
export { RowTextAlign, isolateSelectedRows } from "./rowTextAlign";
export { TEXT_COLOR_NAMES, TEXT_COLORS, TextColors, badgeStyle } from "./textColors";
export type { BadgeVariant, TextColorName, TextColorsOptions } from "./textColors";

/**
 * Tiptap rich text editor shell built on the deci-ui toolbar and buttons.
 *
 * The caller owns the editor (`useEditor` from `@tiptap/react`) and its
 * extensions, and composes the toolbar from the controls below, so a consumer
 * can add its own control (see `RichTextEditor.Control`) next to the builtins:
 *
 * ```tsx
 * const editor = useEditor({ extensions: [StarterKit, TextColors, RowTextAlign], content });
 * <RichTextEditor editor={editor}>
 *   <RichTextEditor.Toolbar>
 *     <RichTextEditor.Group>
 *       <RichTextEditor.TurnInto />
 *       <RichTextEditor.Lists />
 *     </RichTextEditor.Group>
 *     <RichTextEditor.Group>
 *       <RichTextEditor.Color />
 *       <RichTextEditor.Bold />
 *       <RichTextEditor.Italic />
 *     </RichTextEditor.Group>
 *   </RichTextEditor.Toolbar>
 *   <RichTextEditor.Content />
 * </RichTextEditor>
 * ```
 *
 * The builtin controls assume StarterKit (bold, italic, underline, strike,
 * headings, lists, blockquote, code block, link). `Color` needs `TextColors`,
 * and `Align` (and the single align buttons) need `RowTextAlign`, which aligns
 * only the rows at the selection. Hovering a link in the content shows a card
 * to open, copy, edit or remove it.
 */

export interface RichTextEditorProps {
  editor: Editor | null;
  /** Disables every toolbar control bound to the editor. Content editability stays with the editor itself. */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

const RichTextEditorRoot = ({ editor, disabled = false, className, children }: RichTextEditorProps) => (
  <RichTextEditorContext.Provider value={{ editor, disabled }}>
    <div
      className={clsx(styles.root, className)}
      data-deci-rich-text-editor="true"
      data-focus-ring="within"
    >
      {children}
    </div>
  </RichTextEditorContext.Provider>
);

const isFillSpace = (node: React.ReactNode) => React.isValidElement(node) && node.type === Toolbar.FillSpace;

const RichTextEditorToolbar = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  // A divider between each group, so callers only list groups. A `FillSpace`
  // pushes the groups after it to the right, and gets no divider either side.
  const groups = React.Children.toArray(children).filter(Boolean);
  return (
    <Toolbar
      className={clsx(styles.toolbar, className)}
      role="toolbar"
      {...props}
    >
      {groups.map((group, index) => (
        <React.Fragment key={index}>
          {index > 0 && !isFillSpace(group) && !isFillSpace(groups[index - 1]) ? <Toolbar.Divider /> : null}
          {group}
        </React.Fragment>
      ))}
    </Toolbar>
  );
};

export interface RichTextEditorContentProps {
  className?: string;
  /** Shows a card to open, copy, edit or remove a link when it's hovered. Default true. */
  linkHoverCard?: boolean;
}

const RichTextEditorContent = ({ className, linkHoverCard = true }: RichTextEditorContentProps) => {
  const { editor } = useRichTextEditor();
  const hoverCard = useLinkHoverCard();
  return (
    <>
      <EditorContent
        editor={editor}
        className={clsx(styles.content, className)}
        {...(linkHoverCard ? hoverCard.contentProps : {})}
      />
      {linkHoverCard ? hoverCard.card : null}
    </>
  );
};

export interface RichTextEditorControlProps {
  /** Accessible name and tooltip. */
  label: string;
  icon: IconName;
  /** Pressed state, re-evaluated on every editor transaction. */
  isActive?: (editor: Editor) => boolean;
  /** Disabled state, re-evaluated on every editor transaction. */
  isDisabled?: (editor: Editor) => boolean;
  onClick: (editor: Editor) => void;
}

/** One toolbar button bound to the editor. The builtin controls are all instances of this. */
const RichTextEditorControl = ({ label, icon, isActive, isDisabled, onClick }: RichTextEditorControlProps) => {
  const { editor, disabled } = useRichTextEditor();
  const state = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      active: current && isActive ? isActive(current) : false,
      disabled: !current || (isDisabled ? isDisabled(current) : false),
    }),
  });

  return (
    <Button
      icon
      size="sm"
      label={label}
      selected={state?.active ?? false}
      disabled={disabled || (state?.disabled ?? true)}
      onMouseDown={preserveSelection}
      onClick={() => editor && onClick(editor)}
    >
      <Icon name={icon} />
    </Button>
  );
};

type Level = 1 | 2 | 3;

const control = (props: RichTextEditorControlProps) => {
  const Builtin = () => <RichTextEditorControl {...props} />;
  Builtin.displayName = `RichTextEditor.${props.label.replace(/\s+/g, "")}`;
  return Builtin;
};

const heading = (level: Level) =>
  control({
    label: `Heading ${level}`,
    icon: `heading-${level}`,
    isActive: (editor) => editor.isActive("heading", { level }),
    onClick: (editor) => editor.chain().focus().toggleHeading({ level }).run(),
  });

const align = (alignment: "left" | "center" | "right" | "justify") =>
  control({
    label: `Align ${alignment}`,
    icon: `align-${alignment}`,
    isActive: (editor) => editor.isActive({ textAlign: alignment }),
    onClick: (editor) => editor.chain().focus().setTextAlign(alignment).run(),
  });

const RichTextEditor = Object.assign(RichTextEditorRoot, {
  Toolbar: RichTextEditorToolbar,
  Group: Toolbar.Group,
  FillSpace: Toolbar.FillSpace,
  Content: RichTextEditorContent,
  Control: RichTextEditorControl,
  /** Dropdown of block options; see `RichTextEditorMenuProps`. */
  Menu: RichTextEditorMenu,
  /** Block type menu: Text, headings, code block, quote. */
  TurnInto: RichTextEditorTurnInto,
  /** List menu: bulleted, numbered. */
  Lists: RichTextEditorLists,
  /** Alignment menu: left, center, right, justify. */
  Align: RichTextEditorAlign,
  /** Text colour, highlight and badge popover. */
  Color: RichTextEditorColor,
  Bold: control({
    label: "Bold",
    icon: "bold",
    isActive: (editor) => editor.isActive("bold"),
    onClick: (editor) => editor.chain().focus().toggleBold().run(),
  }),
  Italic: control({
    label: "Italic",
    icon: "italic",
    isActive: (editor) => editor.isActive("italic"),
    onClick: (editor) => editor.chain().focus().toggleItalic().run(),
  }),
  Underline: control({
    label: "Underline",
    icon: "underline",
    isActive: (editor) => editor.isActive("underline"),
    onClick: (editor) => editor.chain().focus().toggleUnderline().run(),
  }),
  Strikethrough: control({
    label: "Strikethrough",
    icon: "strikethrough",
    isActive: (editor) => editor.isActive("strike"),
    onClick: (editor) => editor.chain().focus().toggleStrike().run(),
  }),
  H1: heading(1),
  H2: heading(2),
  H3: heading(3),
  BulletList: control({
    label: "Bullet list",
    icon: "list",
    isActive: (editor) => editor.isActive("bulletList"),
    onClick: (editor) => editor.chain().focus().toggleBulletList().run(),
  }),
  OrderedList: control({
    label: "Ordered list",
    icon: "list-ordered",
    isActive: (editor) => editor.isActive("orderedList"),
    onClick: (editor) => editor.chain().focus().toggleOrderedList().run(),
  }),
  Blockquote: control({
    label: "Quote",
    icon: "quote",
    isActive: (editor) => editor.isActive("blockquote"),
    onClick: (editor) => editor.chain().focus().toggleBlockquote().run(),
  }),
  Link: RichTextEditorLink,
  AlignLeft: align("left"),
  AlignCenter: align("center"),
  AlignRight: align("right"),
  AlignJustify: align("justify"),
});

export default RichTextEditor;
